import { Controller } from "@hotwired/stimulus"

// Vet card keyboard flow (SPT_Discovery_Line_Plan.md section 3), built on
// the contact-review pattern: server-truth forms, this controller only
// routes keys to requestSubmit().
//
//   1-9      select a slot (no arm timer; the highlight is the bookmark the
//            operator returns to after a tool tab). Since the owner register
//            of 2026-09-30 the slots run in Drew's order: artist.tools,
//            Chartmetric, SubmitHub.
//   O        open the slot's tool in a new tab + copy that slot's playlist
//            link (clipboard write inside the anchor click handler BEFORE
//            the tab opens; success flips the label for 3s; failure focuses
//            a select()'d readonly input; the URL is always visible as text)
//   G/N/Y/R  record what the tool said, via the matching tiny turbo:false
//            form in the selected slot. When a source carries TWO red
//            verdicts, R arms a numbered pick (1/2) instead of guessing.
//   B/F/N    on an artist.tools slot: pick the bot label in its reading
//            form and move to the numbers (the reading form controller
//            handles the rest; Enter saves).
//   T        write the story
//   P / W    open the selected playlist / the curator profile on Spotify
//   V (V)    the decide button. Two-press inside ARM_WINDOW_MS for the
//            destructive identities; a "go to next check" identity is
//            single-press NAVIGATION (moves the slot highlight, no submit).
//   X, 1-9   arm the not-a-fit reason pick, then choose
//   ->       skip (session cycle)   Z  undo (server-side reopen)
//   Esc      clear selection + disarm everything
//
// Enter NEVER accepts (contact-review guard, copied): the decide button is
// type=button and a focused Enter inside the card's action area is eaten.
//
// Inside an open "Note a spike or drop" form (owner 2026-11-03) no card key
// works at all, so a key pressed on its drop zone or button can never record
// a reading; opening the form puts the cursor in its date box.
export default class extends Controller {
  static targets = ["slot", "checkForm", "verdictButton", "decideForm", "decideButton",
    "skipForm", "undoForm", "reasonPanel", "reasonForm", "noteInput",
    "copyAnchor", "copyFallback", "closeForm", "playlistLink", "profileLink",
    "readingForm", "storyBox"]
  static values = { selected: Number, ownerUrl: String }

  ARM_WINDOW_MS = 3000

  connect() {
    this.decideArmed = false
    this.redArmed = false
    this.reasonArmed = false
    this.closeConfirmed = false
    if (this.hasDecideButtonTarget) this.decideLabel = this.decideButtonTarget.textContent
    this.keydown = this.handleKey.bind(this)
    document.addEventListener("keydown", this.keydown)
    if (this.selectedValue) this.select(this.selectedValue)
  }

  disconnect() {
    document.removeEventListener("keydown", this.keydown)
    clearTimeout(this.armTimer)
  }

  handleKey(event) {
    // Never steal keys from any editable surface, or from the spike note form.
    if (event.target.closest("input, textarea, select, [contenteditable], .spt-vet-spike-open")) return
    if (document.activeElement && document.activeElement.isContentEditable) return
    if (event.metaKey || event.ctrlKey || event.altKey) return

    const key = event.key
    if (key === "Enter") {
      // Enter NEVER accepts - even via a focused decide button.
      if (event.target.closest(".spt-vet-decide-row, .spt-vet-slot")) event.preventDefault()
      return
    }

    if (key >= "1" && key <= "9") {
      const n = parseInt(key, 10)
      if (this.reasonArmed) { this.pickReason(n); event.preventDefault(); return }
      if (this.redArmed) { this.pickRed(n); event.preventDefault(); return }
      this.select(n); event.preventDefault()
    } else if (key === "Escape") {
      this.disarmAll()
    } else if (["b", "B", "f", "F", "n", "N"].includes(key) && this.selectedSource() === "artist_tools") {
      this.chooseBotLabel(key.toUpperCase())
      event.preventDefault()
    } else if (key === "t" || key === "T") {
      this.focusStory()
      event.preventDefault()
    } else if (["g", "G", "n", "N", "y", "Y", "r", "R"].includes(key)) {
      this.recordKey(key.toUpperCase())
      event.preventDefault()
    } else if (key === "o" || key === "O") {
      this.openTool()
      event.preventDefault()
    } else if (key === "p" || key === "P") {
      this.openSelectedUrl()
      event.preventDefault()
    } else if (key === "w" || key === "W") {
      this.openProfile()
      event.preventDefault()
    } else if (key === "v" || key === "V") {
      this.pressDecide()
      event.preventDefault()
    } else if (key === "x" || key === "X") {
      this.armReasons()
      event.preventDefault()
    } else if (key === "ArrowRight") {
      if (this.hasSkipFormTarget) this.skipFormTarget.requestSubmit()
    } else if (key === "z" || key === "Z") {
      if (this.hasUndoFormTarget) this.undoFormTarget.requestSubmit()
    }
  }

  // --- slot selection (the context-switch bookmark) ---

  select(n) {
    const slot = this.slotTargets.find(el => parseInt(el.dataset.slotNumber, 10) === n)
    if (!slot) return
    this.disarmPicks()
    this.selectedValue = n
    this.slotTargets.forEach(el => el.classList.remove("spt-vet-slot-selected"))
    slot.classList.add("spt-vet-slot-selected")
  }

  selectedSlotEl() {
    return this.slotTargets.find(el => parseInt(el.dataset.slotNumber, 10) === this.selectedValue)
  }

  selectedSource() {
    const slot = this.selectedSlotEl()
    return slot ? slot.dataset.source : null
  }

  // --- "Note a spike or drop" ---

  // The details element's toggle: on open, the cursor goes to the date box
  // unless it already holds a date (a refused save puts it on the drop zone).
  spikeOpened(event) {
    const details = event.currentTarget
    if (!details.open) return
    const date = details.querySelector("input[type=date]")
    if (date && !date.value) date.focus()
  }

  // --- the artist.tools reading form ---

  chooseBotLabel(key) {
    const form = this.readingFormTargets.find(f => parseInt(f.dataset.slot, 10) === this.selectedValue)
    if (!form) return
    const controller = this.application.getControllerForElementAndIdentifier(form, "spt-artist-tools-form")
    if (controller) controller.chooseBotLabel(key)
  }

  focusStory() {
    if (!this.hasStoryBoxTarget) return
    this.disarmAll()
    this.storyBoxTarget.scrollIntoView({ block: "center" })
    this.storyBoxTarget.focus()
  }

  // --- recording lights ---

  recordKey(key) {
    const forms = this.checkFormTargets.filter(f =>
      parseInt(f.dataset.slot, 10) === this.selectedValue && f.dataset.key === key)
    if (forms.length === 0) return
    if (key === "R" && forms.length > 1) {
      this.armRedPick(forms)
      return
    }
    forms[0].requestSubmit()
  }

  armRedPick(forms) {
    // Full disarm, not just picks: an armed decide (V pressed once) must not
    // survive into the red pick, or the next V would submit instantly.
    this.disarmAll()
    this.redArmed = true
    this.redForms = forms
    forms.forEach(f => {
      const btn = f.querySelector("[data-red-index]")
      if (btn) btn.classList.add("spt-vet-armed")
    })
    this.armTimer = setTimeout(() => this.disarmPicks(), this.ARM_WINDOW_MS)
  }

  pickRed(n) {
    const form = (this.redForms || []).find(f => parseInt(f.dataset.redIndex, 10) === n)
    if (form) form.requestSubmit()
    this.disarmPicks()
  }

  // --- open tool + copy (clipboard BEFORE the tab opens) ---

  openTool() {
    const anchor = this.copyAnchorTargets.find(a => parseInt(a.dataset.slot, 10) === this.selectedValue)
    if (anchor) anchor.click()
  }

  copyOpen(event) {
    event.preventDefault()
    const anchor = event.currentTarget
    const url = anchor.dataset.copyUrl
    let write
    try {
      write = navigator.clipboard && navigator.clipboard.writeText
        ? navigator.clipboard.writeText(url)
        : Promise.reject(new Error("no clipboard"))
    } catch (e) {
      write = Promise.reject(e)
    }
    // The tab opens in the same user gesture, AFTER the write is initiated.
    window.open(anchor.href, "_blank", "noopener")
    write.then(
      () => this.flipLabel(anchor),
      () => this.focusFallback(anchor)
    )
  }

  flipLabel(anchor) {
    const label = anchor.querySelector("[data-role='label']")
    if (!label) return
    const original = label.textContent
    label.textContent = "Link copied"
    setTimeout(() => { label.textContent = original }, 3000)
  }

  focusFallback(anchor) {
    const input = this.copyFallbackTargets.find(i => i.dataset.slot === anchor.dataset.slot)
    if (!input) return
    input.hidden = false
    input.focus()
    input.select()
  }

  // --- links ---

  openSelectedUrl() {
    const slot = this.selectedSlotEl()
    const url = slot && slot.dataset.url
    if (url) window.open(url, "_blank", "noopener")
  }

  openProfile() {
    if (this.ownerUrlValue) window.open(this.ownerUrlValue, "_blank", "noopener")
  }

  // --- the decide button (identity from the server) ---

  pressDecide() {
    if (!this.hasDecideButtonTarget) return
    const presses = parseInt(this.decideButtonTarget.dataset.presses, 10)
    if (presses === 1) {
      // "Go to next check" / "Write the story": pure navigation, nothing submits.
      if (this.decideButtonTarget.dataset.focus === "story") { this.focusStory(); return }
      const next = parseInt(this.decideButtonTarget.dataset.nextSlot, 10)
      if (next) this.select(next)
      return
    }
    if (this.decideArmed) {
      if (this.hasDecideFormTarget) this.decideFormTarget.requestSubmit()
      return
    }
    this.disarmAll()
    this.decideArmed = true
    this.decideButtonTarget.classList.add("spt-vet-armed")
    this.decideButtonTarget.textContent = "Press V again to confirm"
    this.armTimer = setTimeout(() => this.disarmAll(), this.ARM_WINDOW_MS)
  }

  // --- not-a-fit reasons ---

  armReasons() {
    if (!this.hasReasonPanelTarget) return
    this.disarmAll()
    this.reasonArmed = true
    this.reasonPanelTarget.hidden = false
    this.reasonPanelTarget.scrollIntoView({ block: "nearest" })
  }

  pickReason(n) {
    const form = this.reasonFormTargets.find(f => parseInt(f.dataset.reasonKey, 10) === n)
    if (!form) return
    if (form.dataset.reasonOther) {
      // "Other" needs its note typed first: focus, never auto-submit.
      if (this.hasNoteInputTarget) this.noteInputTarget.focus()
      return
    }
    form.requestSubmit()
  }

  // --- close as botted: confirm at the action step via the app modal ---

  confirmClose(event) {
    if (this.closeConfirmed) return
    event.preventDefault()
    const form = event.currentTarget
    const button = form.querySelector("[data-confirm-message]")
    const message = button ? button.dataset.confirmMessage : "Close this curator as botted?"
    window.appConfirm(message, { confirmLabel: "Close as botted" }).then(ok => {
      if (!ok) return
      this.closeConfirmed = true
      form.requestSubmit()
    })
  }

  // --- disarm ---

  disarmPicks() {
    clearTimeout(this.armTimer)
    this.redArmed = false
    this.verdictButtonTargets.forEach(b => b.classList.remove("spt-vet-armed"))
  }

  disarmAll() {
    this.disarmPicks()
    if (this.decideArmed && this.hasDecideButtonTarget) {
      this.decideButtonTarget.classList.remove("spt-vet-armed")
      this.decideButtonTarget.textContent = this.decideLabel
    }
    this.decideArmed = false
    if (this.reasonArmed && this.hasReasonPanelTarget) this.reasonPanelTarget.hidden = true
    this.reasonArmed = false
  }
}
